import { Injectable } from "@angular/core";
import * as imageCompression from "browser-image-compression";
import { ImageCompressService } from "../shared/image-compress.service";
import { PictureData } from "./canvas/canvas.component";
import { ImageService } from "./image.service";
import { Coord, StrokeData } from "./stroke-data";

@Injectable({
  providedIn: "root",
})
export class AnnotationService {
  private current: Coord;
  private strokes: Coord[];
  private curX: number;
  private curY: number;
  private originalImg: HTMLImageElement;
  private displayedImg: HTMLImageElement;
  private angle: number;
  private tempCanvas: HTMLCanvasElement;
  private evt: number;
  private drawEnabled: boolean;
  private lineWidthFactor: number;
  private nodeWidthFactor: number;
  public rectReset = [];

  constructor(private _imageCompressor: ImageCompressService) {
    this.strokes = [];
    this.angle = 0;
    this.evt = 0;
    this.drawEnabled = true;
    this.lineWidthFactor = 250000;
    this.nodeWidthFactor = this.lineWidthFactor / 2.5;
  }

  // > DEBUG
  setDebugImg(debugImg: HTMLImageElement) {
    this.displayedImg = debugImg;
  }

  setDebugCanvas(debugCanvas: HTMLCanvasElement) {
    this.tempCanvas = debugCanvas;
  }

  debug(msg: string) {
    this.evt++;
    var now = new Date();
    console.info(
      `${this.evt} ${now.getSeconds()}.${now.getMilliseconds()}: ${msg}`
    );
  }
  // dEBUG <

  // > INTERACTION

  touchMove(event: TouchEvent) {
    this.curX = event.touches[0].pageX;
    this.curY = event.touches[0].pageY;
    let canvas = event.target as HTMLCanvasElement;
    this.drawLine(this.getTouchCoord(event, canvas), canvas, "click");
  }

  mouseDown(event: MouseEvent) {
    let canvas = event.target as HTMLCanvasElement;

    if (this.drawEnabled) {
    }
  }

  mouseClick(event: MouseEvent) {
    let canvas = event.target as HTMLCanvasElement;

    if (this.drawEnabled)
      // this.debug('click');
      this.drawLine(this.getMouseCoord(event, canvas), canvas, "click");
  }

  mouseDblClick(event: MouseEvent) {
    let canvas = event.target as HTMLCanvasElement;

    this.undoLastStroke(canvas);
    this.undoLastStroke(canvas);

    this.drawEnabled = false;
    // this.debug('dblClick');

    this.closeShape(this.getMouseCoord(event, canvas), canvas);
  }

  mouseUp(event: MouseEvent) {
    let canvas = event.target as HTMLCanvasElement;

    if (this.drawEnabled) {
    }
  }
  // iNTERACTION <

  undoLastStroke(canvas: HTMLCanvasElement) {
    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;

    // this.debug(`undoLast-Start: ${this.strokes.length}\r${JSON.stringify(this.strokes)}`);
    this.strokes.pop();
    let ctx = canvas.getContext("2d");

    this.redrawImage(canvas, ctx);

    this.redrawLines(ctx, canvasWidth, canvasHeight);

    this.current = this.strokes[this.strokes.length - 1];

    // this.debug(`undoLast-End: ${this.strokes.length}`);

    this.drawEnabled = true;
  }

  reset(canvas: HTMLCanvasElement) {
    // this.debug(`undoLast-Start: ${this.strokes.length}\r${JSON.stringify(this.strokes)}`);
    this.strokes = [];
    let ctx = canvas.getContext("2d");

    this.redrawImage(canvas, ctx);

    this.current = null;

    // this.debug(`undoLast-End: ${this.strokes.length}`);

    this.drawEnabled = true;
  }

  clearImage(canvas: HTMLCanvasElement) {
    this.angle = 0;
    this.displayedImg = undefined;
  }

  rotate(canvas: HTMLCanvasElement, clockwise: boolean): any {
    let ctx = canvas.getContext("2d");
    // ctx.clearRect(0, 0, canvasWidth, canvasHeight);

    const angle = clockwise ? 0.25 : -0.25;

    this.angle += angle;

    const tilted = this.angle % 0.5 !== 0;

    this.tempCanvas =
      this.tempCanvas || canvas.ownerDocument.createElement("canvas");

    this.rotateLines(clockwise);
    this.rotateImage(tilted, canvas, ctx, this.originalImg);
  }

  flip(canvas: HTMLCanvasElement, horizontal: boolean): void {
    let ctx = canvas.getContext("2d");

    this.tempCanvas =
      this.tempCanvas || canvas.ownerDocument.createElement("canvas");

    this.flipLines(horizontal);

    this.flipImage(horizontal, canvas, ctx, this.originalImg);
  }

  getImageUrl(canvas: HTMLCanvasElement): string {
    return canvas.toDataURL("image/jpeg", 0.75);
  }
  getCanvasBlob(canvas: HTMLCanvasElement): Promise<File> {
    return imageCompression.default.canvasToFile(
      canvas,
      "image/jpeg",
      "modified",
      Date.now()
    );
  }
  getImageBlob(img: HTMLImageElement): Promise<File> {
    var canvas = document.createElement("canvas");
    canvas.width = img.width;
    canvas.height = img.height;
    var ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0);
    return imageCompression.default.canvasToFile(
      canvas,
      "image/jpeg",
      "modified",
      Date.now()
    );
  }

  loadImg(
    src: File | string,
    canvas: HTMLCanvasElement,
    onloadHandler?: () => void
  ): void {
    var url = "";
    if (src instanceof Blob) url = URL.createObjectURL(src);
    else url = src;
    this.originalImg = new Image();
    this.originalImg.crossOrigin = "Anonymous";
    this.originalImg.src = url;
    this.displayedImg = this.displayedImg || new Image();
    this.displayedImg.crossOrigin = "Anonymous";
    this.displayedImg.src = url;
    let that = this;

    let ctx = canvas.getContext("2d");

    this.displayedImg.onload = function () {
      new ImageService().loadImg(that.displayedImg, src, canvas, onloadHandler);
      if (src instanceof Blob) URL.revokeObjectURL(url);
    };
  }

  setCoords(coords: Coord[], canvas): void {
    delete coords[coords.findIndex((item) => item.X == 0 && item.Y == 0)];
    this.strokes = coords;

    if (coords.length > 0) {
      this.current = coords[coords.length - 1];

      if (this.current.X == coords[0].X && this.current.Y == coords[0].Y)
        this.drawEnabled = false;
    }

    let ctx = canvas.getContext("2d");

    this.redrawLines(ctx, canvas.width, canvas.height);
  }

  async getPictureData(canvas: HTMLCanvasElement): Promise<PictureData> {
    if (this.originalImg.src.startsWith("http"))
      return new Promise<PictureData>(async (resolve) =>
        resolve({
          originalBlob: await this.getImageBlob(this.originalImg),
          blob: await this.getCanvasBlob(canvas),
          // points: this.strokes,
        } as PictureData)
      );

    var blob = await this.getCanvasBlob(canvas);
    return {
      originalBlob: blob,
      blob: blob,
      // points: this.strokes,
    } as PictureData;
  }

  // > PRIVATE
  private redrawImage(
    canvas: HTMLCanvasElement,
    ctx: CanvasRenderingContext2D
  ) {
    if (this.displayedImg != undefined)
      new ImageService().drawImage(this.displayedImg, canvas, ctx);
  }

  private redrawLines(
    ctx: CanvasRenderingContext2D,
    canvasWidth: number,
    canvasHeight: number
  ) {
    this.debug("redrawLines");

    setTimeout(() => {
      let lastCoord: Coord;
      for (let coord of this.strokes) {
        if (lastCoord != null) {
          let stroke = new StrokeData(lastCoord, coord);
          this.drawStroke(ctx, canvasWidth, canvasHeight, stroke);
        }
        lastCoord = coord;
      }
    }, 10);
  }

  private rotateImage(
    tilted: boolean,
    canvas: HTMLCanvasElement,
    ctx: CanvasRenderingContext2D,
    img: HTMLImageElement
  ) {
    // const canvasWidth = canvas.width;
    // const canvasHeight = canvas.height;
    let svc = new ImageService();
    svc.rotateImage(
      img,
      this.angle,
      canvas.ownerDocument,
      (blob: File) => {
        this.debug("rotateImage: rotated");

        svc.loadImg(this.displayedImg, blob, canvas, async () => {
          this.debug("rotateImage: loaded");

          canvas.width = tilted ? img.height : img.width;
          canvas.height = tilted ? img.width : img.height;

          this.originalImg.src = this.displayedImg.src;
          this.angle = 0;

          await this._imageCompressor
            .handleCanvasToFile(this.tempCanvas)
            .then((blob) =>
              svc.loadImg(this.displayedImg, blob, canvas, () => {
                this.redrawLines(ctx, canvas.width, canvas.height);
              })
            );
        });
      },
      this.tempCanvas
    );
  }

  private flipImage(
    horizontal: boolean,
    canvas: HTMLCanvasElement,
    ctx: CanvasRenderingContext2D,
    img: HTMLImageElement
  ) {
    let that = this;
    let svc = new ImageService();

    svc.flipImage(
      img,
      horizontal,
      canvas.ownerDocument,
      (blob: File) => {
        this.debug("flipImage: flipped");

        svc.loadImg(that.displayedImg, blob, canvas, async () => {
          this.debug("flipImage: loaded");

          that.originalImg.src = that.displayedImg.src;

          await this._imageCompressor
            .handleCanvasToFile(this.tempCanvas)
            .then((blob) =>
              svc.loadImg(this.displayedImg, blob, canvas, () => {
                this.redrawLines(ctx, canvas.width, canvas.height);
              })
            );
        });
      },
      this.tempCanvas
    );
  }

  private getTouchCoord(event: TouchEvent, canvas: HTMLCanvasElement) {
    var rect = canvas.getBoundingClientRect();
    var x = event.targetTouches[0].pageX - rect.left;
    var y = event.targetTouches[0].pageY - rect.top;
    return new Coord(x / canvas.clientWidth, y / canvas.clientHeight);
  }

  private getMouseCoord(event: MouseEvent, canvas: HTMLCanvasElement) {
    return new Coord(
      event.offsetX / canvas.clientWidth,
      event.offsetY / canvas.clientHeight
    );
  }

  private startDraw(coord: Coord, canvas: HTMLCanvasElement) {
    this.current = coord;
  }

  private drawLine(
    coord: Coord,
    canvas: HTMLCanvasElement,
    sourceEvent: string
  ) {
    if (this.current == null) {
      this.current = coord;
      this.strokes.push(coord);

      let ctx = canvas.getContext("2d");
      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;
      let scaleWidth = ctx.canvas.clientWidth / canvasWidth;
      let scaleHeight = ctx.canvas.clientHeight / canvasHeight;
      let scale = Math.sqrt(scaleWidth * scaleHeight);

      ctx.beginPath();
      ctx.fillStyle = "#ff0";
      ctx.arc(
        coord.X * canvasWidth,
        coord.Y * canvasHeight,
        (canvasWidth * canvasHeight * scale) / this.nodeWidthFactor,
        0,
        2 * Math.PI,
        false
      );
      ctx.stroke();

      return;
    }
    const stroke = new StrokeData(this.current, coord);
    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;

    // console.info(`Strokes-a-${sourceEvent}: ${JSON.stringify(this.strokes)}`);
    this.strokes.push(coord);
    // console.info(`Strokes-b-${sourceEvent}: ${JSON.stringify(this.strokes)}`);

    let ctx = canvas.getContext("2d");

    this.drawStroke(ctx, canvasWidth, canvasHeight, stroke);

    this.current = coord;
  }

  private closeShape(coord: Coord, canvas: HTMLCanvasElement) {
    if (this.current == null) {
      this.current = coord;
      return;
    }
    const stroke = new StrokeData(this.current, this.strokes[0]);
    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;

    this.strokes.push(this.strokes[0]);

    let ctx = canvas.getContext("2d");

    this.drawStroke(ctx, canvasWidth, canvasHeight, stroke);

    // this.current = coord;
  }

  private endDraw(coord: Coord, canvas: HTMLCanvasElement): void {
    const stroke = new StrokeData(this.current, coord);
    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;

    this.strokes.push(coord);

    let ctx = canvas.getContext("2d");

    this.drawStroke(ctx, canvasWidth, canvasHeight, stroke);
  }

  private drawStroke(
    ctx: CanvasRenderingContext2D,
    canvasWidth: number,
    canvasHeight: number,
    stroke: StrokeData
  ) {
    ctx.beginPath();

    let scaleWidth = ctx.canvas.clientWidth / canvasWidth;
    let scaleHeight = ctx.canvas.clientHeight / canvasHeight;
    let scale = Math.sqrt(scaleWidth * scaleHeight);

    ctx.moveTo(stroke.Start.X * canvasWidth, stroke.Start.Y * canvasHeight);
    ctx.lineTo(stroke.End.X * canvasWidth, stroke.End.Y * canvasHeight);
    ctx.strokeStyle = "#ff0";
    ctx.lineWidth = (canvasWidth * canvasHeight * scale) / this.lineWidthFactor; // lineWidth = .2% of the average dimension of the image loaded onto the canvas so lines will always appear to be relatively the same width, even when some images are bigger than others (on a stretched canvas)
    ctx.stroke();

    ctx.beginPath();
    ctx.fillStyle = "#ff0";
    ctx.arc(
      stroke.End.X * canvasWidth,
      stroke.End.Y * canvasHeight,
      (canvasWidth * canvasHeight * scale) / this.nodeWidthFactor,
      0,
      2 * Math.PI,
      false
    );
    ctx.stroke();
  }
  // pRIVATE <

  rotateLines(clockwise: boolean) {
    for (let i = 0; i < this.strokes.length; i++) {
      let coord = this.strokes[i];
      if (clockwise) this.strokes[i] = new Coord(1 - coord.Y, coord.X);
      else this.strokes[i] = new Coord(coord.Y, 1 - coord.X);
    }
  }

  flipLines(horizontal: boolean) {
    for (let i = 0; i < this.strokes.length; i++) {
      let coord = this.strokes[i];
      if (horizontal) this.strokes[i] = new Coord(coord.X, 1 - coord.Y);
      else this.strokes[i] = new Coord(1 - coord.X, coord.Y);
    }
  }

  addCoord(x: number, y: number): void {
    this.strokes.push(new Coord(x, y));
  }

  getCoords(): Coord[] {
    return this.strokes;
  }
}
